import React from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { CheckIcon, XMarkIcon, UserIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';

/**
 * Modal for prompting user to complete profile before first introduction request
 */
const CompleteProfileModal = ({ 
  isOpen, 
  onClose, 
  onProceedAnyway, 
  companyName,
  missingItems = [] 
}) => {
  const navigate = useNavigate();

  const handleCompleteProfile = () => {
    onClose();
    navigate('/profile');
  };

  const getMissingItemDetails = () => {
    const itemDetails = {
      linkedin: {
        icon: UserIcon,
        label: 'LinkedIn Profile',
        description: 'Professional LinkedIn URL'
      },
      resume: {
        icon: DocumentTextIcon,
        label: 'Resume Upload',
        description: 'Current resume document'
      }
    };

    return missingItems
      .filter(item => item === 'linkedin' || item === 'resume')
      .map(item => itemDetails[item])
      .filter(Boolean);
  };

  const missingItemDetails = getMissingItemDetails();

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25 dark:bg-black dark:bg-opacity-50" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between mb-4">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-semibold leading-6 text-gray-900 dark:text-gray-100"
                  >
                    One more step to a great intro!
                  </Dialog.Title>
                  <button
                    type="button"
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <div className="mb-6">
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                    Adding your Resume and LinkedIn profile gives our connectors the context they need to make the best possible introduction for you. 
                  </p>
                  <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800/50 rounded-lg p-3 mb-4">
                    <p className="text-sm text-purple-700 dark:text-purple-300">
                      <strong>💡 Pro tip:</strong> Profiles with this information are 5x more likely to result in a meeting.
                    </p>
                  </div>
                </div>

                {/* Missing Items Checklist */}
                {missingItemDetails.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      Complete these items for better results:
                    </h4>
                    <div className="space-y-2">
                      {missingItemDetails.map((item, index) => {
                        const IconComponent = item.icon;
                        return (
                          <div key={index} className="flex items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                            <div className="flex-shrink-0 mr-3">
                              <div className="w-5 h-5 border-2 border-gray-300 dark:border-gray-500 rounded-full flex items-center justify-center">
                                <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                              </div>
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center">
                                <IconComponent className="h-4 w-4 text-gray-500 dark:text-gray-400 mr-2" />
                                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                  {item.label}
                                </span>
                              </div>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                {item.description}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="flex flex-col space-y-3">
                  <button
                    type="button"
                    className="w-full inline-flex justify-center items-center rounded-lg border border-transparent bg-purple-600 px-4 py-3 text-sm font-semibold text-white hover:bg-purple-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2 dark:bg-purple-700 dark:hover:bg-purple-600 transition-colors"
                    onClick={handleCompleteProfile}
                  >
                    <UserIcon className="h-4 w-4 mr-2" />
                    Complete Profile
                  </button>
                  <button
                    type="button"
                    className="w-full inline-flex justify-center rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2 transition-colors"
                    onClick={onProceedAnyway}
                  >
                    Request Intro Anyway
                  </button>
                </div>

                <div className="mt-4 text-center">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Connecting with <span className="font-semibold text-gray-700 dark:text-gray-300">{companyName}</span>
                  </p>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default CompleteProfileModal; 